/**
 * Auto-Post Queue — admin view of every scheduled post across all clients.
 *
 * The publisher tick (hourly default — see backend/routers/publisher.py)
 * walks this queue and dispatches due rows to the per-platform publishers.
 * Today every publisher returns `oauth_not_wired` so failures here are
 * expected until LinkedIn / X / TikTok / Pinterest OAuth is wired in.
 *
 * Per-row admin actions: Retry (resets to queued, optionally reschedule)
 * and Cancel (only available while status is still 'queued').
 */
import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  CalendarClock, RefreshCw, AlertCircle, CheckCircle2, XCircle, Loader2,
  PlayCircle, Send,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  queued:    { label: "Queued",    cls: "bg-amber-50 text-amber-700 border-amber-200",  Icon: CalendarClock },
  sent:      { label: "Sent",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  failed:    { label: "Failed",    cls: "bg-red-50 text-red-700 border-red-200",        Icon: AlertCircle },
  skipped:   { label: "Skipped",   cls: "bg-zinc-50 text-zinc-700 border-zinc-200",     Icon: XCircle },
  cancelled: { label: "Cancelled", cls: "bg-zinc-50 text-zinc-500 border-zinc-200",     Icon: XCircle },
};

const PLATFORM_COLORS = {
  facebook: "#1877F2", instagram: "#E1306C", linkedin: "#0A66C2",
  x: "#000000", twitter: "#000000", tiktok: "#010101",
  pinterest: "#E60023", youtube: "#FF0000",
};

export default function AutoPostQueuePage() {
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [tickResult, setTickResult] = useState(null);

  const load = () => {
    const q = new URLSearchParams();
    if (statusFilter) q.set("status", statusFilter);
    q.set("limit", "200");
    api.get(`/auto-post/queue?${q.toString()}`)
      .then((r) => setRows(r.data?.rows || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load queue"));
  };

  useEffect(() => {
    load();
    // Live-ish — refresh every 30s while the page is open.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const runTick = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/auto-post/tick`);
      setTickResult(data);
      toast.success(`Tick: ${data.sent} sent · ${data.failed} failed · ${data.skipped} skipped`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tick failed");
    } finally { setBusy(false); }
  };

  const retry = async (row) => {
    try {
      await api.post(`/auto-post/queue/${row.queue_id}/retry`, {});
      toast.success("Re-queued");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); }
  };

  const cancel = async (row) => {
    try {
      await api.post(`/auto-post/queue/${row.queue_id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };

  const stats = useMemo(() => {
    const r = rows || [];
    return {
      queued:  r.filter((x) => x.status === "queued").length,
      sent:    r.filter((x) => x.status === "sent").length,
      failed:  r.filter((x) => x.status === "failed").length,
      total:   r.length,
    };
  }, [rows]);

  return (
    <div className="p-6 lg:p-8 max-w-7xl space-y-6" data-testid="auto-post-queue-page">
      <header>
        <div className="label-overline text-[#002FA7] flex items-center gap-2">
          <Send className="h-3 w-3" /> Auto-post publisher
        </div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight mt-2">
          Scheduled posts queue
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Activities you marked <strong>Queue for auto-posting</strong> in AI
          Studio land here. The hourly tick (or the Run-now button below)
          dispatches each row to the matching platform publisher. Platform
          publishers will fail with <code className="font-mono">oauth_not_wired</code>
          {" "}until the per-client OAuth flows ship — the orchestration is
          ready, only the actual API call remains.
        </p>
      </header>

      <Card className="rounded-sm">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <Stat label="Queued"  value={stats.queued}  color="text-amber-700" />
          <Stat label="Sent"    value={stats.sent}    color="text-emerald-700" />
          <Stat label="Failed"  value={stats.failed}  color="text-red-700" />
          <Stat label="Total"   value={stats.total}   color="text-zinc-700" />
          <div className="ml-auto flex items-center gap-2">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-sm border border-zinc-200 bg-white px-2 text-xs"
              data-testid="auto-post-status-filter">
              <option value="">All statuses</option>
              <option value="queued">Queued</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <Button onClick={runTick} disabled={busy}
              className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
              data-testid="auto-post-tick-now">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
              Run tick now
            </Button>
          </div>
          {tickResult && (
            <div className="basis-full text-[11px] font-mono text-zinc-500 pt-2 border-t border-zinc-100 mt-1">
              Last manual tick: processed={tickResult.processed} · sent={tickResult.sent}
              {" "}· failed={tickResult.failed} · skipped={tickResult.skipped}
              {" "}· at {new Date(tickResult.tick_at).toLocaleString()}
            </div>
          )}
        </CardContent>
      </Card>

      {rows === null ? (
        <div className="text-sm text-zinc-500 flex items-center gap-2 p-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading queue…
        </div>
      ) : rows.length === 0 ? (
        <Card className="rounded-sm border-dashed">
          <CardContent className="p-10 text-center text-sm text-zinc-500">
            No queued posts. Finalise an AI Studio activity with{" "}
            <strong>Queue for auto-posting</strong> ticked and it will land here.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const meta = STATUS_META[r.status] || STATUS_META.queued;
            const Icon = meta.Icon;
            const dueIso = r.scheduled_at;
            const due = dueIso ? new Date(dueIso) : null;
            const isDue = due && due.getTime() <= Date.now();
            return (
              <Card key={r.queue_id} className="rounded-sm" data-testid={`queue-row-${r.queue_id}`}>
                <CardContent className="p-3 flex items-start gap-3">
                  <div className="rounded-sm w-9 h-9 grid place-items-center text-white text-[10px] font-bold"
                    style={{ background: PLATFORM_COLORS[r.platform] || "#71717A" }}>
                    {(r.platform || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase">{r.platform}</span>
                      <Badge className={`rounded-sm font-mono text-[10px] border ${meta.cls}`}>
                        <Icon className="h-3 w-3 mr-1" /> {meta.label}
                      </Badge>
                      {r.attempts > 0 && (
                        <span className="text-[10px] font-mono text-zinc-500">
                          attempts: {r.attempts}
                        </span>
                      )}
                      {isDue && r.status === "queued" && (
                        <span className="text-[10px] font-mono text-red-700">⚠ overdue</span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      <span className="font-mono">{r.client_id}</span> ·{" "}
                      due {due ? due.toLocaleString() : "—"} ·{" "}
                      <span className="font-mono">{r.activity_id}</span>
                    </div>
                    <p className="text-xs text-zinc-700 mt-1.5 line-clamp-2">
                      {r.post_copy || <span className="italic text-zinc-400">No copy</span>}
                    </p>
                    {r.last_error && (
                      <div className="text-[11px] text-red-700 font-mono mt-1">
                        {r.last_error}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {r.status === "failed" && (
                      <Button size="sm" variant="outline" onClick={() => retry(r)}
                        className="rounded-sm h-7 text-[11px]"
                        data-testid={`queue-retry-${r.queue_id}`}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Retry
                      </Button>
                    )}
                    {r.status === "queued" && (
                      <Button size="sm" variant="ghost" onClick={() => cancel(r)}
                        className="rounded-sm h-7 text-[11px] text-zinc-500"
                        data-testid={`queue-cancel-${r.queue_id}`}>
                        <XCircle className="h-3 w-3 mr-1" /> Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white px-3 py-2 min-w-[80px]">
      <div className="label-overline">{label}</div>
      <div className={`text-2xl font-display font-extrabold ${color}`}>{value}</div>
    </div>
  );
}
